// Admin → Prospects — retailer sales pipeline (kanban + list).
//
// Backend (modules/admin_prospects.js, mounted /api/admin/prospects):
//   GET    /admin/prospects?status=active|lost|converted|all
//   POST   /admin/prospects                  — create
//   GET    /admin/prospects/:id              — one + notes
//   PATCH  /admin/prospects/:id              — edit details / move stage
//   POST   /admin/prospects/:id/notes        — add a note
//   POST   /admin/prospects/:id/lost         — archive { reason }
//   POST   /admin/prospects/:id/reopen       — un-archive
//   POST   /admin/prospects/:id/convert      — at 'live' → creates a retailer
//
// Inbound website enquiries (POST /api/public/prospect-enquiry) land here at
// stage "contacted". Dark-amber theme, mobile-friendly, daily-use.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

type Stage = "contacted" | "demo_booked" | "demo_done" | "onboarding" | "live";

interface Prospect {
  id: string;
  businessName: string;
  contactName: string;
  phone: string;
  email: string;
  location: string;
  notes: string;
  source: string;
  stage: Stage;
  nextAction: string;
  nextActionDue: string | null;
  status: "active" | "lost" | "converted";
  lostReason: string;
  convertedRetailerId: string | null;
  stageChangedAt: string;
  createdAt: string;
  daysInStage: number;
  daysSinceAdded: number;
  noteCount?: number;
}

interface ListResp { ok: boolean; stages: Stage[]; prospects: Prospect[] }

const STAGES: { key: Stage; label: string }[] = [
  { key: "contacted",  label: "Contacted" },
  { key: "demo_booked", label: "Demo Booked" },
  { key: "demo_done",  label: "Demo Done" },
  { key: "onboarding", label: "Onboarding" },
  { key: "live",       label: "Live" },
];
const STAGE_KEYS = STAGES.map((s) => s.key);

function esc(s: string): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}
function fmtDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
// Due-date urgency: overdue (past), soon (today/tomorrow), or normal.
function dueClass(iso: string | null): string {
  if (!iso) return "";
  const due = new Date(iso + "T00:00:00").getTime();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.floor((due - today.getTime()) / 86400000);
  if (days < 0) return "prosp-due--over";
  if (days <= 1) return "prosp-due--soon";
  return "";
}

const prospectsPage: Page = {
  name: "prospects",
  label: "Prospects",
  icon: "🎯",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let prospects: Prospect[] = [];
    let view: "kanban" | "list" = "kanban";
    let statusFilter: "active" | "lost" = "active";
    let loading = true;
    let loadErr = "";

    const root = document.createElement("section");
    root.className = "prosp-page";
    container.appendChild(root);

    // ── data ────────────────────────────────────────────────────────────────
    async function load(): Promise<void> {
      loading = true; loadErr = ""; render();
      try {
        const data = await ctx.api<ListResp>(
          `/admin/prospects?status=${statusFilter}`, { signal: ac.signal });
        prospects = data.prospects || [];
      } catch (e) {
        if ((e as { name?: string }).name === "AbortError") return;
        loadErr = e instanceof ApiError ? e.message : "Failed to load prospects";
      } finally {
        loading = false; render();
      }
    }

    // ── card + column markup ──────────────────────────────────────────────────
    function cardHtml(p: Prospect): string {
      const contact = [p.contactName, p.location].filter(Boolean).map(esc).join(" · ");
      const comms = [p.phone, p.email].filter(Boolean).map(esc).join(" · ");
      const due = p.nextActionDue
        ? `<span class="prosp-due ${dueClass(p.nextActionDue)}">${esc(fmtDate(p.nextActionDue))}</span>` : "";
      const next = p.nextAction
        ? `<div class="prosp-next">▸ ${esc(p.nextAction)} ${due}</div>` : "";
      const notes = p.notes ? `<div class="prosp-cardnotes">${esc(p.notes)}</div>` : "";
      const srcBadge = p.source ? `<span class="prosp-src">${esc(p.source)}</span>` : "";
      const convertBtn = p.stage === "live"
        ? `<button class="prosp-mini prosp-mini--go" data-act="convert" data-id="${p.id}">Convert →</button>` : "";
      return `
        <article class="prosp-card" data-id="${p.id}" data-stage="${p.stage}" tabindex="0">
          <div class="prosp-card-head">
            <h4>${esc(p.businessName)}</h4>${srcBadge}
          </div>
          ${contact ? `<div class="prosp-line">${contact}</div>` : ""}
          ${comms ? `<div class="prosp-line prosp-muted">${comms}</div>` : ""}
          ${next}
          ${notes}
          <div class="prosp-card-foot">
            <span class="prosp-meta">${p.daysInStage}d in stage · added ${esc(fmtDate(p.createdAt))}</span>
            ${p.noteCount ? `<span class="prosp-meta">📝 ${p.noteCount}</span>` : ""}
          </div>
          <div class="prosp-actions">
            <button class="prosp-mini" data-act="edit" data-id="${p.id}">Edit</button>
            <button class="prosp-mini" data-act="note" data-id="${p.id}">Note</button>
            ${statusFilter === "active"
              ? `<button class="prosp-mini prosp-mini--danger" data-act="lost" data-id="${p.id}">Lost</button>`
              : `<button class="prosp-mini" data-act="reopen" data-id="${p.id}">Reopen</button>`}
            ${convertBtn}
          </div>
        </article>`;
    }

    function render(): void {
      const head = `
        <header class="prosp-header">
          <div>
            <h1>Prospects</h1>
            <p class="prosp-sub">Retailer sales pipeline — inbound enquiries land in Contacted.</p>
          </div>
          <div class="prosp-controls">
            <div class="prosp-seg" role="tablist" aria-label="View">
              <button data-view="kanban" class="${view === "kanban" ? "active" : ""}">Kanban</button>
              <button data-view="list" class="${view === "list" ? "active" : ""}">List</button>
            </div>
            <div class="prosp-seg" role="tablist" aria-label="Status">
              <button data-status="active" class="${statusFilter === "active" ? "active" : ""}">Active</button>
              <button data-status="lost" class="${statusFilter === "lost" ? "active" : ""}">Lost</button>
            </div>
            <button class="adm-btn" data-act="add">+ Add prospect</button>
          </div>
        </header>`;

      let body: string;
      if (loading) body = `<div class="adm-loading">Loading…</div>`;
      else if (loadErr) body = `<div class="adm-error">${esc(loadErr)}</div>`;
      else if (!prospects.length) body = `<div class="adm-empty">No ${statusFilter} prospects yet.</div>`;
      else if (view === "kanban") {
        body = `<div class="prosp-board">` + STAGES.map((s) => {
          const cards = prospects.filter((p) => p.stage === s.key);
          return `
            <div class="prosp-col" data-stage="${s.key}">
              <div class="prosp-col-head">${s.label}<span class="prosp-count">${cards.length}</span></div>
              <div class="prosp-col-body">${cards.map(cardHtml).join("") || `<div class="prosp-col-empty">—</div>`}</div>
            </div>`;
        }).join("") + `</div>`;
      } else {
        body = `<div class="prosp-list">` + STAGES.map((s) => {
          const cards = prospects.filter((p) => p.stage === s.key);
          if (!cards.length) return "";
          return `<div class="prosp-list-group"><div class="prosp-list-stage">${s.label} (${cards.length})</div>${cards.map(cardHtml).join("")}</div>`;
        }).join("") + `</div>`;
      }
      root.innerHTML = head + body;
    }

    // ── modal helper ──────────────────────────────────────────────────────────
    function openModal(title: string, inner: string, onSubmit: (form: HTMLFormElement) => Promise<void>): void {
      const ov = document.createElement("div");
      ov.className = "prosp-modal-ov";
      ov.innerHTML = `
        <form class="prosp-modal" novalidate>
          <div class="prosp-modal-head"><h3>${esc(title)}</h3>
            <button type="button" class="prosp-x" data-close aria-label="Close">×</button></div>
          <div class="prosp-modal-body">${inner}</div>
          <div class="prosp-modal-foot">
            <button type="button" class="adm-btn-ghost" data-close>Cancel</button>
            <button type="submit" class="adm-btn">Save</button>
          </div>
        </form>`;
      const close = (): void => { ov.remove(); document.removeEventListener("keydown", onKey); };
      const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };
      ov.addEventListener("mousedown", (e) => { if (e.target === ov) close(); });
      ov.querySelectorAll("[data-close]").forEach((b) => b.addEventListener("click", close));
      const form = ov.querySelector("form") as HTMLFormElement;
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
        btn.disabled = true;
        void onSubmit(form).then(close).catch((err) => {
          btn.disabled = false;
          ctx.showToast(err instanceof ApiError ? err.message : "Save failed", "error");
        });
      });
      document.addEventListener("keydown", onKey);
      document.body.appendChild(ov);
      (ov.querySelector("input,textarea,select") as HTMLElement | null)?.focus();
    }

    function field(label: string, name: string, value = "", type = "text"): string {
      const v = esc(value);
      const input = type === "textarea"
        ? `<textarea name="${name}" class="adm-textarea" rows="3">${v}</textarea>`
        : `<input name="${name}" class="adm-input" type="${type}" value="${v}" />`;
      return `<label class="prosp-fld"><span>${esc(label)}</span>${input}</label>`;
    }
    function stageSelect(current: Stage): string {
      const opts = STAGES.map((s) => `<option value="${s.key}" ${s.key === current ? "selected" : ""}>${s.label}</option>`).join("");
      return `<label class="prosp-fld"><span>Stage</span><select name="stage" class="adm-select">${opts}</select></label>`;
    }
    function formBody(p?: Prospect): string {
      return `
        ${field("Business name *", "businessName", p?.businessName)}
        ${field("Contact name", "contactName", p?.contactName)}
        <div class="prosp-fld-row">${field("Phone", "phone", p?.phone)}${field("Email", "email", p?.email, "email")}</div>
        ${field("Location", "location", p?.location)}
        <div class="prosp-fld-row">${field("Next action", "nextAction", p?.nextAction)}${field("Due date", "nextActionDue", p?.nextActionDue ?? "", "date")}</div>
        ${field("Notes", "notes", p?.notes, "textarea")}
        ${stageSelect(p?.stage ?? "contacted")}`;
    }
    function readForm(form: HTMLFormElement): Record<string, string> {
      const fd = new FormData(form);
      const o: Record<string, string> = {};
      fd.forEach((v, k) => { o[k] = String(v).trim(); });
      return o;
    }

    function openAdd(): void {
      openModal("Add prospect", formBody(), async (form) => {
        const b = readForm(form);
        if (!b.businessName) throw new ApiError(400, "Business name is required");
        await ctx.api("/admin/prospects", { method: "POST", body: JSON.stringify(b), signal: ac.signal });
        ctx.showToast("Prospect added", "success");
        await load();
      });
    }
    function openEdit(p: Prospect): void {
      openModal(`Edit — ${p.businessName}`, formBody(p), async (form) => {
        const b = readForm(form);
        if (!b.businessName) throw new ApiError(400, "Business name is required");
        await ctx.api(`/admin/prospects/${encodeURIComponent(p.id)}`, {
          method: "PATCH", body: JSON.stringify(b), signal: ac.signal });
        ctx.showToast("Saved", "success");
        await load();
      });
    }
    function openNote(p: Prospect): void {
      openModal(`Add note — ${p.businessName}`,
        `<label class="prosp-fld"><span>Note</span><textarea name="body" class="adm-textarea" rows="4"></textarea></label>`,
        async (form) => {
          const body = readForm(form).body;
          if (!body) throw new ApiError(400, "Note is required");
          await ctx.api(`/admin/prospects/${encodeURIComponent(p.id)}/notes`, {
            method: "POST", body: JSON.stringify({ body }), signal: ac.signal });
          ctx.showToast("Note added", "success");
          await load();
        });
    }
    function openLost(p: Prospect): void {
      openModal(`Mark lost — ${p.businessName}`,
        `<p class="prosp-modal-note">Archived, not deleted — you can reopen it later.</p>
         <label class="prosp-fld"><span>Reason (optional)</span><textarea name="reason" class="adm-textarea" rows="2"></textarea></label>`,
        async (form) => {
          await ctx.api(`/admin/prospects/${encodeURIComponent(p.id)}/lost`, {
            method: "POST", body: JSON.stringify({ reason: readForm(form).reason }), signal: ac.signal });
          ctx.showToast("Marked as lost", "success");
          await load();
        });
    }
    async function reopen(p: Prospect): Promise<void> {
      try {
        await ctx.api(`/admin/prospects/${encodeURIComponent(p.id)}/reopen`, { method: "POST", signal: ac.signal });
        ctx.showToast("Reopened", "success");
        await load();
      } catch (e) { ctx.showToast(e instanceof ApiError ? e.message : "Failed", "error"); }
    }
    async function convert(p: Prospect): Promise<void> {
      if (!window.confirm(`Convert "${p.businessName}" into a live retailer account?`)) return;
      try {
        const r = await ctx.api<{ ok: boolean; slug: string }>(
          `/admin/prospects/${encodeURIComponent(p.id)}/convert`, { method: "POST", signal: ac.signal });
        ctx.showToast(`Converted → retailer "${r.slug}"`, "success");
        await load();
      } catch (e) { ctx.showToast(e instanceof ApiError ? e.message : "Convert failed", "error"); }
    }

    // ── async stage move (drag drop / used by drag) ─────────────────────────────
    async function moveStage(p: Prospect, stage: Stage): Promise<void> {
      const prev = p.stage;
      p.stage = stage; p.daysInStage = 0; render();   // optimistic
      try {
        await ctx.api(`/admin/prospects/${encodeURIComponent(p.id)}`, {
          method: "PATCH", body: JSON.stringify({ stage }), signal: ac.signal });
      } catch (e) {
        p.stage = prev; render();
        ctx.showToast(e instanceof ApiError ? e.message : "Move failed", "error");
      }
    }

    // ── click delegation ────────────────────────────────────────────────────────
    function onClick(e: MouseEvent): void {
      if (suppressClick) { suppressClick = false; return; }
      const t = e.target as HTMLElement;
      const viewBtn = t.closest<HTMLElement>("[data-view]");
      if (viewBtn) { view = viewBtn.dataset["view"] as "kanban" | "list"; render(); return; }
      const statusBtn = t.closest<HTMLElement>("[data-status]");
      if (statusBtn) { statusFilter = statusBtn.dataset["status"] as "active" | "lost"; void load(); return; }
      const actBtn = t.closest<HTMLElement>("[data-act]");
      if (!actBtn) return;
      const act = actBtn.dataset["act"];
      if (act === "add") { openAdd(); return; }
      const id = actBtn.dataset["id"];
      const p = prospects.find((x) => x.id === id);
      if (!p) return;
      if (act === "edit") openEdit(p);
      else if (act === "note") openNote(p);
      else if (act === "lost") openLost(p);
      else if (act === "reopen") void reopen(p);
      else if (act === "convert") void convert(p);
    }
    root.addEventListener("click", onClick);

    // ── drag-to-move (pointer: mouse + touch), kanban only ──────────────────────
    const DRAG_THRESHOLD = 6;
    let drag: { pointerId: number; startX: number; startY: number; id: string;
      origin: HTMLElement; ghost: HTMLElement | null; active: boolean; ox: number; oy: number } | null = null;
    let suppressClick = false;

    function clearTargets(): void {
      root.querySelectorAll(".prosp-col--drop").forEach((el) => el.classList.remove("prosp-col--drop"));
    }
    function endDrag(): void {
      if (!drag) return;
      drag.ghost?.remove();
      drag.origin.classList.remove("prosp-card--dragging");
      document.body.classList.remove("prosp-dragging");
      clearTargets();
      drag = null;
    }
    function onDown(e: PointerEvent): void {
      if (view !== "kanban") return;
      if (e.button !== undefined && e.button !== 0) return;
      const card = (e.target as HTMLElement | null)?.closest<HTMLElement>(".prosp-card");
      if (!card) return;
      if ((e.target as HTMLElement).closest("[data-act]")) return; // let action buttons click
      const id = card.dataset["id"]; if (!id) return;
      const r = card.getBoundingClientRect();
      drag = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, id, origin: card,
        ghost: null, active: false, ox: e.clientX - r.left, oy: e.clientY - r.top };
      try { card.setPointerCapture(e.pointerId); } catch { /* detached node */ }
    }
    function onMove(e: PointerEvent): void {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
      if (!drag.active) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        drag.active = true;
        const r = drag.origin.getBoundingClientRect();
        const g = drag.origin.cloneNode(true) as HTMLElement;
        g.classList.add("prosp-card--ghost");
        g.style.cssText = `position:fixed;left:${r.left}px;top:${r.top}px;width:${r.width}px;pointer-events:none;z-index:9999;`;
        document.body.appendChild(g);
        drag.ghost = g;
        drag.origin.classList.add("prosp-card--dragging");
        document.body.classList.add("prosp-dragging");
      }
      if (drag.ghost) {
        drag.ghost.style.left = `${e.clientX - drag.ox}px`;
        drag.ghost.style.top = `${e.clientY - drag.oy}px`;
      }
      const prevDisp = drag.ghost?.style.display ?? "";
      if (drag.ghost) drag.ghost.style.display = "none";
      const below = document.elementFromPoint(e.clientX, e.clientY);
      if (drag.ghost) drag.ghost.style.display = prevDisp;
      clearTargets();
      const col = below?.closest<HTMLElement>(".prosp-col");
      if (col && root.contains(col)) col.classList.add("prosp-col--drop");
      if (e.pointerType === "touch") e.preventDefault();
    }
    function onUp(e: PointerEvent): void {
      if (!drag || drag.pointerId !== e.pointerId) return;
      const wasActive = drag.active; const id = drag.id;
      let target: HTMLElement | null = null;
      if (wasActive) {
        const prevDisp = drag.ghost?.style.display ?? "";
        if (drag.ghost) drag.ghost.style.display = "none";
        const below = document.elementFromPoint(e.clientX, e.clientY);
        if (drag.ghost) drag.ghost.style.display = prevDisp;
        target = below?.closest<HTMLElement>(".prosp-col") ?? null;
      }
      endDrag();
      if (wasActive) {
        suppressClick = true;
        const key = target?.dataset["stage"];
        const p = prospects.find((x) => x.id === id);
        if (p && key && STAGE_KEYS.includes(key as Stage) && p.stage !== key) {
          void moveStage(p, key as Stage);
        }
      }
    }
    function onCancel(): void { endDrag(); }
    root.addEventListener("pointerdown", onDown);
    root.addEventListener("pointermove", onMove);
    root.addEventListener("pointerup", onUp);
    root.addEventListener("pointercancel", onCancel);

    void load();

    return () => {
      ac.abort();
      root.removeEventListener("click", onClick);
      root.removeEventListener("pointerdown", onDown);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerup", onUp);
      root.removeEventListener("pointercancel", onCancel);
      endDrag();
      root.remove();
    };
  },
};

registerPage(prospectsPage);
export default prospectsPage;
